import {  t  } from "@/client/libs/dummy-i18n";
import { DownloadSimple, Eye, TrendUp, Users } from "@phosphor-icons/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@stackmorph/ui";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";

import { axios } from "@/client/libs/axios";

export const AdminDashboardPage = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: async () => {
      const response = await axios.get("/admin/stats", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      return response.data;
    },
  });

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">{t`Loading...`}</div>;
  }

  return (
    <>
      <Helmet>
        <title>{t`Admin Dashboard - Stackmorph Resume Builder`}</title>
      </Helmet>

      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-3xl font-bold">{t`Admin Dashboard`}</h1>
          <p className="text-muted-foreground">
            {t`System overview and management`}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t`Total Users`}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalUsers ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                {stats?.todaySignups ?? 0} {t`new today`}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t`Premium Users`}</CardTitle>
              <TrendUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.premiumUsers ?? 0}</div>
              <p className="text-xs text-muted-foreground">
                {stats?.conversionRate?.toFixed(1) ?? 0}% {t`conversion rate`}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t`Total Resumes`}</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.totalResumes ?? 0}</div>
              <p className="text-xs text-muted-foreground">{t`Created by users`}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t`Monthly Revenue`}</CardTitle>
              <DownloadSimple className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ₹{stats?.monthlyRevenue?.toLocaleString() ?? 0}
              </div>
              <p className="text-xs text-muted-foreground">
                ₹{stats?.totalRevenue?.toLocaleString() ?? 0} {t`total`}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t`Quick Actions`}</CardTitle>
            <CardDescription>{t`Common administrative tasks`}</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <a
              href="/admin/users"
              className="rounded-lg border p-4 transition-colors hover:bg-secondary"
            >
              <h3 className="font-medium">{t`Manage Users`}</h3>
              <p className="text-sm text-muted-foreground">
                {t`View and edit user accounts`}
              </p>
            </a>
            <a
              href="/admin/premium"
              className="rounded-lg border p-4 transition-colors hover:bg-secondary"
            >
              <h3 className="font-medium">{t`Premium Management`}</h3>
              <p className="text-sm text-muted-foreground">
                {t`Grant and manage premium access`}
              </p>
            </a>
            <a
              href="/admin/logs"
              className="rounded-lg border p-4 transition-colors hover:bg-secondary"
            >
              <h3 className="font-medium">{t`Audit Logs`}</h3>
              <p className="text-sm text-muted-foreground">{t`View system activity`}</p>
            </a>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
